import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, TrendingUp, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
const fullMonthLabels = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
const expenseTypes = new Set([
  'maintenance_fee',
  'maintenance',
  'expense',
  'repair',
  'utilities',
  'operational',
  'other_expense',
]);

const PropertyDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { propertyName } = useParams();
  const decodedPropertyName = decodeURIComponent(propertyName || '');
  const [owner, setOwner] = useState(null);
  const [property, setProperty] = useState(null);
  const [allChartData, setAllChartData] = useState([]);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const ownerData = localStorage.getItem('owner');

    if (!token || !ownerData) {
      navigate('/login');
      return;
    }

    setOwner(JSON.parse(ownerData));
    fetchPropertyData(token);
  }, [decodedPropertyName, navigate]);

  const fetchPropertyData = async (token) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [propertiesRes, revenueRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboard/properties`, config),
        axios.get(`${BACKEND_URL}/api/dashboard/revenue`, config)
      ]);

      const matchedProperty = (propertiesRes.data || []).find((item) => item.name === decodedPropertyName);
      const fullSeries = buildMonthlySeries(revenueRes.data || [], decodedPropertyName);
      setProperty(matchedProperty || null);
      setAllChartData(fullSeries);

      const defaultWindow = fullSeries.slice(-6);
      setRangeStart(defaultWindow[0]?.key || fullSeries[0]?.key || '');
      setRangeEnd(defaultWindow[defaultWindow.length - 1]?.key || fullSeries[fullSeries.length - 1]?.key || '');
    } catch (error) {
      console.error('Error fetching property detail:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('owner');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const buildMonthlySeries = (transactions, targetPropertyName) => {
    const propertyTransactions = transactions.filter((transaction) => transaction.property_name === targetPropertyName);
    const now = new Date();
    let startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    let endDate = new Date(now.getFullYear(), now.getMonth(), 1);

    propertyTransactions.forEach((transaction) => {
      const date = new Date(transaction.transaction_date);
      if (Number.isNaN(date.getTime())) return;
      const monthDate = new Date(date.getFullYear(), date.getMonth(), 1);
      if (monthDate < startDate) startDate = monthDate;
      if (monthDate > endDate) endDate = monthDate;
    });

    const monthSeries = [];
    const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

    while (cursor <= endDate) {
      monthSeries.push({
        key: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`,
        label: `${monthLabels[cursor.getMonth()]} ${String(cursor.getFullYear()).slice(-2)}`,
        longLabel: `${fullMonthLabels[cursor.getMonth()]} ${cursor.getFullYear()}`,
        year: cursor.getFullYear().toString(),
        monthIndex: cursor.getMonth().toString(),
        income: 0,
        expense: 0,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }

    const monthLookup = monthSeries.reduce((acc, item, index) => {
      acc[item.key] = index;
      return acc;
    }, {});

    propertyTransactions.forEach((transaction) => {
        const date = new Date(transaction.transaction_date);
        if (Number.isNaN(date.getTime())) return;

        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const index = monthLookup[key];
        if (index === undefined) return;

        if (['deposit', 'deposit_settled', 'pengembalian_deposit'].includes(transaction.category)) return;
        const amount = parseFloat(transaction.amount) || 0;
        const transactionType = (transaction.type || transaction.transaction_type || '').toLowerCase();

        if (expenseTypes.has(transactionType) || transaction.transaction_type === 'expense') {
          monthSeries[index].expense += amount;
        } else {
          monthSeries[index].income += amount;
        }
      });

    return monthSeries.map((item) => ({
      ...item,
      net: item.income - item.expense,
      margin: item.income > 0 ? ((item.income - item.expense) / item.income) * 100 : 0,
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const chartData = allChartData.filter((item) => {
    if (!rangeStart || !rangeEnd) return true;
    return item.key >= rangeStart && item.key <= rangeEnd;
  });
  const monthOptions = allChartData.map((item) => ({
    value: item.key,
    label: item.longLabel,
  }));
  const latestMonth = chartData[chartData.length - 1] || { income: 0, expense: 0, net: 0 };
  const totalRangeIncome = chartData.reduce((sum, item) => sum + item.income, 0);
  const totalRangeExpense = chartData.reduce((sum, item) => sum + item.expense, 0);
  const handleRangeStartChange = (value) => {
    setRangeStart(value);
    if (rangeEnd && value > rangeEnd) {
      setRangeEnd(value);
    }
  };
  const handleRangeEndChange = (value) => {
    setRangeEnd(value);
    if (rangeStart && value < rangeStart) {
      setRangeStart(value);
    }
  };

  const openRevenueReport = (item) => {
    navigate('/dashboard', {
      state: {
        activeTab: 'revenue',
        selectedProperty: decodedPropertyName,
        selectedMonth: item.monthIndex,
        selectedYear: item.year,
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-isk-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-isk-teal border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-isk-muted">Memuat detail properti...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-isk-bg">
      <div className="glass-nav border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm text-isk-muted">Dashboard Owner</p>
            <h1 className="text-xl sm:text-2xl font-bold text-isk-text truncate">{decodedPropertyName}</h1>
            <p className="text-sm text-isk-muted truncate">{property?.location || 'Detail performa properti'}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-isk-text">{owner?.name}</p>
              <p className="text-xs text-isk-muted">{owner?.email}</p>
            </div>
            <Button
              type="button"
              onClick={() => navigate('/dashboard', {
                state: { activeTab: location.state?.activeTab || 'properties' }
              })}
              variant="outline"
              className="border-2 border-isk-teal/30 text-isk-teal hover:bg-isk-teal hover:text-white rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Kembali
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-card rounded-2xl p-5">
            <div className="w-12 h-12 rounded-xl bg-isk-teal/10 flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-isk-teal" />
            </div>
            <p className="text-sm text-isk-muted">Total Pendapatan</p>
            <p className="text-2xl font-bold text-isk-text mt-1">{formatCurrency(totalRangeIncome)}</p>
          </div>
          <div className="glass-card rounded-2xl p-5">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mb-4">
              <Wallet className="w-6 h-6 text-orange-500" />
            </div>
            <p className="text-sm text-isk-muted">Total Pengeluaran</p>
            <p className="text-2xl font-bold text-isk-text mt-1">{formatCurrency(totalRangeExpense)}</p>
          </div>
          <div className="glass-card rounded-2xl p-5">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm text-isk-muted">Profit Rentang Ini</p>
            <p className="text-2xl font-bold text-isk-text mt-1">{formatCurrency(totalRangeIncome - totalRangeExpense)}</p>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6 sm:p-8">
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-isk-text">Grafik Pendapatan & Pengeluaran</h2>
              <p className="text-sm text-isk-muted mt-1">Pilih rentang waktu untuk melihat tren pendapatan, pengeluaran, dan profit.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-full xl:min-w-[420px]">
              <div className="space-y-1">
                <p className="text-xs font-semibold text-isk-text">Dari</p>
                <Select value={rangeStart} onValueChange={handleRangeStartChange}>
                  <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                    <SelectValue placeholder="Pilih bulan awal" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold text-isk-text">Sampai</p>
                <Select value={rangeEnd} onValueChange={handleRangeEndChange}>
                  <SelectTrigger className="glass-input border-2 focus:border-isk-teal rounded-xl">
                    <SelectValue placeholder="Pilih bulan akhir" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="mb-4 p-4 rounded-2xl bg-isk-dark text-white border border-isk-dark/40 shadow-[0_12px_32px_rgba(15,23,42,0.18)]">
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <span>Total pemasukan: <span className="font-semibold text-isk-teal">{formatCurrency(totalRangeIncome)}</span></span>
              <span>Total pengeluaran: <span className="font-semibold text-isk-gold">{formatCurrency(totalRangeExpense)}</span></span>
              <span>Profit: <span className="font-semibold text-white">{formatCurrency(totalRangeIncome - totalRangeExpense)}</span></span>
            </div>
          </div>

          <div className="h-[380px] rounded-2xl border border-isk-muted/20 bg-isk-bg/90 p-3">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} barGap={14} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(15, 23, 42, 0.16)" vertical={false} />
                <XAxis dataKey="label" stroke="#334155" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis
                  stroke="#334155"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `Rp${(value / 1000000).toFixed(0)}jt`}
                />
                <Tooltip
                  formatter={(value, name, item) => {
                    if (name === 'Margin Profit') {
                      return [`${Number(value || 0).toFixed(1)}%`, name];
                    }
                    if (name === 'Profit') {
                      return [formatCurrency(value), name];
                    }
                    return [formatCurrency(value), name];
                  }}
                  labelFormatter={(label, payload) => {
                    if (!payload?.length) return label;
                    const point = payload[0]?.payload;
                    if (!point) return label;
                    return `${label} • Profit ${formatCurrency(point.net)} • Margin ${Number(point.margin || 0).toFixed(1)}%`;
                  }}
                  contentStyle={{
                    borderRadius: '16px',
                    border: '1px solid rgba(15, 23, 42, 0.18)',
                    backgroundColor: 'rgba(15,23,42,0.96)',
                    color: '#F8FAFC',
                    boxShadow: '0 18px 38px rgba(15,23,42,0.24)',
                  }}
                  labelStyle={{ color: '#F8FAFC', fontWeight: 700 }}
                  itemStyle={{ color: '#E2E8F0' }}
                />
                <Legend />
                <Bar dataKey="income" name="Pendapatan" fill="#0F766E" radius={[10, 10, 0, 0]} />
                <Bar dataKey="expense" name="Pengeluaran" fill="#B45309" radius={[10, 10, 0, 0]} />
                <Line
                  type="monotone"
                  dataKey="net"
                  name="Profit"
                  stroke="#111827"
                  strokeWidth={4}
                  dot={{ r: 4, strokeWidth: 2, fill: '#ffffff', stroke: '#111827' }}
                  activeDot={{ r: 7, strokeWidth: 0, fill: '#111827' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-end justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-bold text-isk-text">Ringkasan 6 Bulan</h3>
              <p className="text-sm text-isk-muted mt-1">Klik bulan untuk membuka tab laporan dengan filter bulan tersebut.</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px]">
              <thead>
                <tr className="border-b border-white/20">
                  <th className="text-left py-3 text-sm font-semibold text-isk-text">Bulan</th>
                  <th className="text-right py-3 text-sm font-semibold text-isk-text">Pendapatan</th>
                  <th className="text-right py-3 text-sm font-semibold text-isk-text">Pengeluaran</th>
                  <th className="text-right py-3 text-sm font-semibold text-isk-text">Net</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((item) => (
                <tr
                    key={item.key}
                    onClick={() => openRevenueReport(item)}
                    className="group border-b border-white/10 cursor-pointer transition-all duration-200 hover:bg-white/60 hover:shadow-[inset_0_0_0_1px_rgba(11,111,99,0.12)]"
                  >
                    <td className="py-3 text-sm text-isk-text font-medium">
                      <div className="flex items-center gap-2">
                        <span className="transition-transform duration-200 group-hover:translate-x-1">{item.label}</span>
                        <span className="text-xs text-isk-muted opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                          Lihat laporan
                        </span>
                      </div>
                    </td>
                    <td className="py-3 text-sm text-right text-green-700">{formatCurrency(item.income)}</td>
                    <td className="py-3 text-sm text-right text-orange-600">{formatCurrency(item.expense)}</td>
                    <td className="py-3 text-sm text-right font-semibold text-isk-text">
                      <div className="flex items-center justify-end gap-3">
                        <span>{formatCurrency(item.net)}</span>
                        <ArrowRight className="w-4 h-4 text-isk-teal opacity-60 transition-all duration-200 group-hover:translate-x-1 group-hover:opacity-100" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
